// Walkthrough-run vocabulary badges.
// Parallel to `components/finding-badges.tsx` — any palette change MUST touch both.
// When a surface renders *both* status and verdict (e.g. row chips, editorial
// headers), prefer `composeWalkthroughTone()` from `experience/vocabulary.ts`.
// Use `<RunStatusBadge>` when status is surfaced on its own (filter labels,
// live-run dots, admin views of a walkthrough in isolation).

import { cn } from "@/lib/utils";
import { Chip, type ChipTone } from "@/components/ui/chip";
import {
  composeWalkthroughTone,
  verdictLabel,
  type Tone,
  type VerdictRating,
  type WalkthroughStatus,
} from "@/components/experience/vocabulary";

const TONE_CHIP: Record<Tone, ChipTone> = {
  helps: "helps",
  critical: "critical",
  important: "important",
  muted: "muted",
  // Chip tone="primary" misses 7:1 on its tint; the spinner marks running.
  running: "neutral",
};

/** Chip tone for a composed walkthrough tone (status + verdict). */
export function toneChipTone(tone: Tone): ChipTone {
  return TONE_CHIP[tone];
}

/**
 * The verdict word ("Yes / Partial / No / N/A") as a toned chip. The tone
 * composes run status and verdict, so a failed run never reads green.
 */
export function VerdictChip({
  rating,
  status = "completed",
  className,
}: {
  rating: VerdictRating;
  status?: WalkthroughStatus;
  className?: string;
}) {
  return (
    <Chip
      tone={TONE_CHIP[composeWalkthroughTone(status, rating)]}
      className={className}
    >
      {verdictLabel(rating)}
    </Chip>
  );
}

export type RunStatus =
  | "pending"
  | "running"
  | "succeeded"
  | "partial"
  | "failed"
  | "cancelled"
  | "blocked";

const STATUS_LABEL: Record<RunStatus, string> = {
  pending: "Pending",
  running: "Running",
  succeeded: "Succeeded",
  partial: "Partial",
  failed: "Failed",
  cancelled: "Cancelled",
  blocked: "Blocked",
};

// Opacity-free semantic tokens so dots stay WCAG-scannable in both themes
// — pending/cancelled use the muted surface token, which reads as "quiet" next
// to the chart-colored active states without relying on opacity on a meaningful
// signal. Running/succeeded/partial/failed pull from the chart/destructive
// palette defined in index.css.
const STATUS_DOT_CLASS: Record<RunStatus, string> = {
  pending: "bg-muted",
  running: "bg-chart-2 animate-pulse",
  succeeded: "bg-chart-1",
  partial: "bg-chart-3",
  failed: "bg-destructive",
  cancelled: "bg-muted",
  blocked: "bg-amber-500",
};

const STATUS_TEXT_CLASS: Record<RunStatus, string> = {
  pending: "text-muted-foreground",
  running: "text-foreground",
  succeeded: "text-foreground",
  partial: "text-foreground",
  failed: "text-destructive",
  cancelled: "text-muted-foreground",
  blocked: "text-amber-600 dark:text-amber-400",
};

const SIZE_CLASS = {
  sm: {
    root: "gap-1.5 text-xs",
    dot: "size-1.5",
  },
  md: {
    root: "gap-2 text-xs",
    dot: "size-2",
  },
} as const;

export interface RunStatusBadgeProps {
  status: RunStatus;
  size?: "sm" | "md";
  className?: string;
}

export function RunStatusBadge({
  status,
  size = "sm",
  className,
}: RunStatusBadgeProps) {
  const sizeCls = SIZE_CLASS[size];
  return (
    <span
      data-slot="run-status-badge"
      data-status={status}
      className={cn(
        "inline-flex items-center font-medium whitespace-nowrap",
        sizeCls.root,
        STATUS_TEXT_CLASS[status],
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "inline-block rounded-full",
          sizeCls.dot,
          STATUS_DOT_CLASS[status],
        )}
      />
      {STATUS_LABEL[status]}
    </span>
  );
}

/** Human-readable label for a run status — for surfaces that want the word without the chip. */
export function runStatusLabel(status: RunStatus): string {
  return STATUS_LABEL[status];
}

/**
 * Map the backend `walkthroughs.status` enum onto the run-status vocabulary.
 * The backend stores `completed`; the badge vocabulary calls the same terminal
 * happy-path state `succeeded` so callers surfacing run-only context don't
 * have to redeclare the mapping.
 */
export function walkthroughStatusToRunStatus(
  status: string,
  blocked?: unknown,
): RunStatus {
  if (blocked) return "blocked";
  if (status === "completed") return "succeeded";
  if (status === "running") return "running";
  if (status === "failed") return "failed";
  if (status === "cancelled") return "cancelled";
  return "pending";
}
